import React, { useState } from "react";
import Avatar from "./Avatar";
import IconPicker, { iconsToList } from "../../admin/IconPicker";
import { apiRequest, errorMessage } from "../../api";
import { SERVICE_ICONS } from "../../data/serviceIcons";
import { cardClass } from "./jobUtils";
import { FiUser, FiPhone, FiGlobe, FiDollarSign, FiBriefcase, FiStar, FiCode, FiAlignLeft, FiSave } from "react-icons/fi";
import { Field, FormAlert, SubmitButton } from "../forms/formKit";

const widePickerClass = "grid grid-cols-2 sm:grid-cols-3 gap-1 max-h-64 overflow-y-auto p-1.5 bg-white/5 border border-navy-border rounded-md";

// Edit the signed-in member's profile: photo, contact details and about text. Sellers also pick the
// services they offer (these power the "Find Seller" filters), their skills and hourly rate.
export default function ProfileForm({ user, onSaved }) {
  const isSeller = user.role === "seller";
  const [form, setForm] = useState({
    full_name: user.full_name || "", phone: user.phone || "", country: user.country || "", company: user.company || "",
    headline: user.headline || "", skills: user.skills || "", services: user.services || "",
    hourly_rate: user.hourly_rate ?? "", bio: user.bio || "",
  });
  const [photo, setPhoto] = useState(null);
  const [removePhoto, setRemovePhoto] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const pickPhoto = (e) => {
    const file = e.target.files[0];
    if (file && file.size > 3 * 1024 * 1024) {
      e.target.value = "";
      return setError("The photo must be 3 MB or smaller.");
    }
    setError("");
    setPhoto(file || null);
    setRemovePhoto(false);
  };

  const toggleService = (key) => {
    const current = iconsToList(form.services);
    setForm({ ...form, services: (current.includes(key) ? current.filter((k) => k !== key) : [...current, key]).join(",") });
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMessage("");
    setError("");
    const fd = new FormData();
    Object.entries(form).forEach(([key, value]) => fd.append(key, value ?? ""));
    if (photo) fd.append("avatar", photo);
    else if (removePhoto) fd.append("avatar", "");   // an empty value removes the saved photo
    const { ok, data } = await apiRequest("/core/cowork/me/", { method: "PATCH", auth: true, body: fd });
    setBusy(false);
    if (!ok) return setError(errorMessage(data));
    setPhoto(null);
    setRemovePhoto(false);
    setMessage("Profile saved.");
    onSaved(data);
  };

  const preview = photo ? URL.createObjectURL(photo) : removePhoto ? null : user.avatar;

  return (
    <form onSubmit={save} className={`${cardClass} p-6 max-w-3xl space-y-5`}>
      <div className="flex flex-wrap items-center gap-5">
        <Avatar src={preview} name={form.full_name || user.email} size={96} />
        <div className="space-y-2">
          <p className="text-sm text-gray-300">Profile photo</p>
          <input type="file" accept="image/*" onChange={pickPhoto} className="text-sm text-gray-300" />
          {(user.avatar || photo) && !removePhoto && (
            <button type="button" onClick={() => { setPhoto(null); setRemovePhoto(true); }} className="block text-xs text-red-400 hover:underline">Remove photo</button>
          )}
          <p className="text-xs text-gray-500">A clear, square photo works best. Max 3 MB.</p>
        </div>
      </div>

      <p className="text-sm text-gray-400">Email: <span className="text-white">{user.email}</span></p>
      <div className="grid md:grid-cols-2 gap-5">
        <Field label="Full name" required icon={FiUser} value={form.full_name} onChange={set("full_name")} />
        <Field label="Phone" icon={FiPhone} value={form.phone} onChange={set("phone")} />
        <Field label="Country" icon={FiGlobe} value={form.country} onChange={set("country")} />
        {isSeller ? (
          <Field label="Hourly rate" icon={FiDollarSign} type="number" min="0" step="0.01" value={form.hourly_rate} onChange={set("hourly_rate")} />
        ) : (
          <Field label="Company / organisation" icon={FiBriefcase} value={form.company} onChange={set("company")} />
        )}
      </div>

      {isSeller && (
        <>
          <Field label="Headline" icon={FiStar} value={form.headline} onChange={set("headline")} placeholder="e.g. Logo & brand designer" />
          <div>
            <label className="block text-sm text-gray-300 mb-2">Services you offer <span className="text-gray-500">(buyers can filter sellers by these)</span></label>
            <IconPicker className={widePickerClass} icons={SERVICE_ICONS} selected={iconsToList(form.services)} onToggle={toggleService} />
          </div>
          <Field label="Skills" icon={FiCode} hint="Comma separated" value={form.skills} onChange={set("skills")} placeholder="Illustrator, Photoshop, Branding" />
        </>
      )}

      <Field as="textarea" label="About you" icon={FiAlignLeft} rows={4} value={form.bio} onChange={set("bio")} />

      {message && <p className="text-sm text-green-400">{message}</p>}
      <FormAlert>{error}</FormAlert>
      <SubmitButton busy={busy} busyLabel="Saving..." icon={FiSave}>Save Profile</SubmitButton>
    </form>
  );
}
